import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Reveal from "@/components/feature/Reveal";
import Gallery from "./components/Gallery";
import { projects } from "@/mocks/projects";

export default function CarmenDeHuechuraba() {
  const project = projects.find((p) => p.slug === "carmen-de-huechuraba") ?? projects[0];

  const entorno = [
    { icon: "ri-graduation-cap-line", label: "Cercanía a colegios" },
    { icon: "ri-shopping-bag-3-line", label: "Cercanía a comercios" },
    { icon: "ri-store-2-line", label: "Cercanía al Mall Plaza Norte" },
    { icon: "ri-route-line", label: "Conectividad con servicios para la vida diaria" },
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="relative w-full min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={project.image}
            alt={`Proyecto ${project.name}`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30" />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pb-14 pt-40">
          <div className="max-w-7xl mx-auto flex flex-col gap-4">
            <span className="inline-flex self-start rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-xs md:text-sm font-semibold px-4 py-1.5 whitespace-nowrap">
              {project.soldLabel}
            </span>
            <h1 className="font-heading text-4xl md:text-6xl text-background-50">
              {project.name}
            </h1>
            <p className="text-sm md:text-base text-background-100/90 flex items-center gap-2">
              <i className="ri-map-pin-line text-primary-300" />
              {project.address}
            </p>
          </div>
        </div>
      </section>

      {/* Información rápida */}
      <section className="px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          <Reveal className="flex flex-col gap-6">
            <span className="text-xs md:text-sm font-semibold uppercase tracking-[0.25em] text-primary-600">
              Información rápida
            </span>
            <div className="grid grid-cols-2 gap-4">
              {project.features.map((feature) => (
                <div
                  key={feature}
                  className="flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-50 p-4"
                >
                  <i className="ri-checkbox-circle-fill text-primary-500 text-xl" />
                  <span className="text-sm md:text-base font-medium text-foreground-800">
                    {feature}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-foreground-600 leading-relaxed">{project.description}</p>
          </Reveal>

          <Reveal delay={100} className="flex flex-col gap-6">
            <span className="text-xs md:text-sm font-semibold uppercase tracking-[0.25em] text-primary-600">
              Concepto arquitectónico
            </span>
            <div className="flex flex-col gap-4 text-foreground-600 leading-relaxed">
              <p>
                El proyecto utiliza un concepto moderno y funcional, con un diseño de estilo
                Mediterráneo que integra elegancia, calidez y una distribución pensada para la
                vida familiar.
              </p>
              <p>
                Cada vivienda está diseñada para aprovechar al máximo la luz natural y los espacios
                comunes, ofreciendo un entorno seguro y tranquilo dentro de un condominio cerrado.
              </p>
            </div>

            <div className="rounded-lg overflow-hidden border border-background-200/70">
              <iframe
                title="Ubicación Carmen de Huechuraba"
                src="https://www.google.com/maps?q=Avenida%20Los%20Libertadores%206811%2C%20Huechuraba%2C%20Chile&output=embed"
                className="w-full h-72 border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Galería */}
      <section className="px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-100/70">
        <div className="max-w-7xl mx-auto flex flex-col gap-10">
          <Reveal className="flex flex-col items-start gap-3">
            <span className="text-xs md:text-sm font-semibold uppercase tracking-[0.25em] text-primary-600">
              Galería
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-900">
              Conoce el proyecto en imágenes
            </h2>
          </Reveal>
          <Gallery
            images={project.gallery}
            name={project.name}
            videoUrl={project.video}
          />
        </div>
      </section>

      {/* Entorno */}
      <section className="px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <div className="max-w-7xl mx-auto flex flex-col gap-10">
          <Reveal className="flex flex-col items-start gap-3">
            <span className="text-xs md:text-sm font-semibold uppercase tracking-[0.25em] text-primary-600">
              Entorno
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-900">
              Ubicación estratégica
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {entorno.map((item, index) => (
              <Reveal key={item.label} delay={index * 80}>
                <div className="flex flex-col gap-3 rounded-lg border border-background-200/70 bg-background-50 p-6 h-full">
                  <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <p className="text-sm font-medium text-foreground-800">{item.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 md:px-8 lg:px-12 py-16 md:py-24 bg-background-100/70">
        <Reveal className="max-w-3xl mx-auto flex flex-col items-center gap-5 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-foreground-900 leading-tight">
            ¿Quieres conocer más sobre Carmen de Huechuraba?
          </h2>
          <Link
            to="/contacto"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 text-sm md:text-base font-semibold px-8 py-3.5 transition-colors duration-300 whitespace-nowrap"
          >
            HABLAR CON UN ASESOR
          </Link>
        </Reveal>
      </section>
    </Layout>
  );
}